import axios from "axios";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { IProviderProps } from "../interfaces/provider";
import { authContext } from "./auth";
import { resolveAvantCareEnvHeader } from "../helpers/avantCareEnv";

interface IApiContext {
    isLoading: boolean
    get: (uri: string, params?: object) => Promise<any>
    getOrThrow: (uri: string, params?: object, timeoutMs?: number) => Promise<any>
    patch: (uri: string, data: object, params?: object) => Promise<any>
    put: (uri: string, data: object, params?: object) => Promise<any>
    putOrThrow: (uri: string, data: object, params?: object, timeoutMs?: number) => Promise<any>
    post: (uri: string, data: object, params?: object) => Promise<any>
    // Variant that treats non-2xx as errors and bubbles them up.
    postOrThrow: (uri: string, data: object, params?: object, timeoutMs?: number) => Promise<any>
    remove: (uri: string, params?: object) => Promise<any>
    removeOrThrow: (uri: string, params?: object, timeoutMs?: number) => Promise<any>
    error?: string
}

const apiContext = createContext<IApiContext>({
    isLoading: false,
    get: async () => ({}),
    getOrThrow: async () => ({}),
    patch: async () => ({}),
    put: async () => ({}),
    putOrThrow: async () => ({}),
    post: async () => ({}),
    postOrThrow: async () => ({}),
    remove: async () => ({}),
    removeOrThrow: async () => ({}),
})

const ApiProvider = (props: IProviderProps) => {
    //const params = useParams<TUrlParams>()

    const orgName = resolveAvantCareEnvHeader()
    const api = useMemo(() => axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
            'Content-Type': 'application/json',
            'X-AvantCare-Env': orgName,
        }
    }), [orgName])
    const [error, setError] = useState<string | undefined>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const { getAccessToken } = useContext(authContext)

    const getConfig = useCallback(async (params?: object) => {
        const acctoken = await getAccessToken()
        return {
            params,
            headers: {
                'Authorization': `Bearer ${acctoken}`
            }
        }
    }, [getAccessToken])

    const get = useCallback(async (uri: string, params?: object): Promise<object> => {
        try {
            const res = await api.get(uri, await getConfig(params))
            if (res.status < 400) {
                return res.data
            } else {
                new Error(res.statusText)
            }
        } catch (e: any) {
            setError(e.message)
        }
        return {}
    }, [api, getConfig])

    const patch = useCallback(async (uri: string, data: object, params?: object): Promise<object> => {
        try {
            const res = await api.patch(uri, data, await getConfig(params))
            //console.log(res)
            if (res.status < 400) {
                return res.data
            } else {
                new Error(res.statusText)
            }
        } catch (e: any) {
            setError(e.message)
        }
        return {}
    }, [api, getConfig])

    const getOrThrow = useCallback(async (uri: string, params?: object, timeoutMs: number = 15000): Promise<object> => {
        try {
            const res = await api.get(uri, {
                ...(await getConfig(params)),
                timeout: timeoutMs,
                validateStatus: (status) => status < 400,
            })
            return res.data
        } catch (e: any) {
            setError(e?.message ?? String(e))
            throw e
        }
    }, [api, getConfig])
    const put = useCallback(async (uri: string, data: object, params?: object): Promise<object> => {
        try {
            const res = await api.put(uri, data, await getConfig(params))
            if (res.status < 400) {
                return res.data
            } else {
                new Error(res.statusText)
            }
        } catch (e: any) {
            setError(e.message)
        }
        return {}
    }, [api, getConfig])

    const putOrThrow = useCallback(async (uri: string, data: object, params?: object, timeoutMs: number = 15000): Promise<object> => {
        try {
            const res = await api.put(uri, data, {
                ...(await getConfig(params)),
                timeout: timeoutMs,
                validateStatus: (status) => status < 400,
            })
            return res.data
        } catch (e: any) {
            setError(e?.message ?? String(e))
            throw e
        }
    }, [api, getConfig])
    const post = useCallback(async (uri: string, data: object, params?: object): Promise<object> => {
        try {
            const res = await api.post(uri, data, await getConfig(params))
            if (res.status < 400) {
                return res.data
            } else {
                new Error(res.statusText)
            }
        } catch (e: any) {
            setError(e.message)
        }
        return {}
    }, [api, getConfig])

    const postOrThrow = useCallback(async (uri: string, data: object, params?: object, timeoutMs: number = 15000): Promise<object> => {
        // Fail fast on non-2xx so callers can retry or show an error.
        try {
            const res = await api.post(uri, data, {
                ...(await getConfig(params)),
                timeout: timeoutMs,
                validateStatus: (status) => status < 400,
            })
            return res.data
        } catch (e: any) {
            setError(e?.message ?? String(e))
            throw e
        }
    }, [api, getConfig])
    const remove = useCallback(async (uri: string, params?: object): Promise<object> => {
        try {
            const res = await api.delete(uri, await getConfig(params))
            if (res.status < 400) {
                return res.data
            } else {
                new Error(res.statusText)
            }
        } catch (e: any) {
            setError(e.message)
        }
        return {}
    }, [api, getConfig])

    const removeOrThrow = useCallback(async (uri: string, params?: object, timeoutMs: number = 15000): Promise<object> => {
        try {
            const res = await api.delete(uri, {
                ...(await getConfig(params)),
                timeout: timeoutMs,
                validateStatus: (status) => status < 400,
            })
            return res.data
        } catch (e: any) {
            setError(e?.message ?? String(e))
            throw e
        }
    }, [api, getConfig])

    const value = useMemo(() => ({
        get,
        getOrThrow,
        patch,
        put,
        putOrThrow,
        post,
        postOrThrow,
        remove,
        removeOrThrow,
        isLoading,
        error
    }), [error, get, getOrThrow, isLoading, patch, post, postOrThrow, put, putOrThrow, remove, removeOrThrow])

    return (
        <apiContext.Provider value={value}>
            {props.children}
        </apiContext.Provider>
    )

}

export { apiContext, ApiProvider }
